import {
  Button,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Dismiss12Regular, Edit12Regular } from '@fluentui/react-icons';
import { useState } from 'react';
import type { Device } from '../../api/types';
import { useAuth } from '../../auth/AuthProvider';
import { errorText } from '../../lib/useRelay';
import { vt } from '../../theme';
import { MAX_TAGS, validateTag } from './model';

const useStyles = makeStyles({
  edit: { minWidth: 0, opacity: 0.6, ':hover': { opacity: 1 } },
  surface: { width: '300px', display: 'flex', flexDirection: 'column', rowGap: '10px', padding: '14px 16px' },
  head: { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' },
  title: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1 },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: '12px', color: vt.fg3 },
  list: { display: 'flex', flexWrap: 'wrap', gap: '6px', minHeight: '22px' },
  tag: {
    display: 'inline-flex',
    alignItems: 'center',
    columnGap: '2px',
    height: '22px',
    paddingInlineStart: '9px',
    paddingInlineEnd: '3px',
    borderRadius: tokens.borderRadiusCircular,
    border: `1px solid ${vt.surface2}`,
    fontSize: '11px',
    color: vt.fg2,
  },
  remove: { minWidth: 0, height: '16px', width: '16px', padding: 0 },
  none: { fontSize: '11px', color: vt.fg3 },
  add: { display: 'flex', columnGap: '8px', alignItems: 'flex-start' },
  input: { flexGrow: 1 },
  foot: { display: 'flex', justifyContent: 'flex-end', columnGap: '8px' },
});

/** Admin tag editing for one device (`PUT /devices/:id/tags`), validated per the contract before it is sent. */
export function TagsPopover({ device, onSaved }: { device: Device; onSaved: () => Promise<void> }) {
  const s = useStyles();
  const { api } = useAuth();
  const [open, setOpen] = useState(false);
  const [tags, setTags] = useState(device.tags);
  const [draft, setDraft] = useState('');
  const [invalid, setInvalid] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const add = () => {
    const tag = draft.trim();
    const why = validateTag(tag, tags);
    setInvalid(why);
    if (why) return;
    setTags([...tags, tag].sort());
    setDraft('');
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await api.setDeviceTags(device.id, tags);
      await onSaved();
      setOpen(false);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  const unchanged = tags.join(',') === device.tags.join(',');

  return (
    <Popover
      open={open}
      onOpenChange={(_e, data) => {
        if (saving) return;
        if (data.open) {
          setTags(device.tags);
          setDraft('');
          setInvalid(null);
          setError(null);
        }
        setOpen(data.open);
      }}
      positioning="below-start"
      trapFocus
    >
      <PopoverTrigger disableButtonEnhancement>
        <Button
          className={s.edit}
          appearance="transparent"
          size="small"
          icon={<Edit12Regular />}
          aria-label={`Edit tags of ${device.name}`}
        />
      </PopoverTrigger>
      <PopoverSurface className={s.surface} aria-label={`Tags of ${device.name}`}>
        <div className={s.head}>
          <span className={s.title}>Tags</span>
          <span className={s.mono}>{device.name}</span>
        </div>
        <div className={s.list}>
          {tags.length === 0 && <span className={s.none}>No tags</span>}
          {tags.map((t) => (
            <span key={t} className={s.tag}>
              {t}
              <Button
                className={s.remove}
                appearance="transparent"
                size="small"
                icon={<Dismiss12Regular />}
                aria-label={`Remove ${t}`}
                onClick={() => setTags(tags.filter((x) => x !== t))}
              />
            </span>
          ))}
        </div>
        <div className={s.add}>
          <Field
            className={s.input}
            validationMessage={invalid}
            validationState={invalid ? 'error' : 'none'}
            hint={invalid ? undefined : `Lowercase letters, digits, hyphens; at most ${MAX_TAGS}.`}
          >
            <Input
              size="small"
              value={draft}
              placeholder="Add a tag"
              aria-label="New tag"
              onChange={(_e, data) => {
                setDraft(data.value);
                setInvalid(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') add();
              }}
            />
          </Field>
          <Button size="small" shape="circular" onClick={add}>
            Add
          </Button>
        </div>
        {error && (
          <MessageBar intent="error">
            <MessageBarBody>{error}</MessageBarBody>
          </MessageBar>
        )}
        <div className={s.foot}>
          <Button
            appearance="primary"
            shape="circular"
            size="small"
            disabled={saving || unchanged}
            icon={saving ? <Spinner size="tiny" /> : undefined}
            onClick={() => void save()}
          >
            Save
          </Button>
          <Button appearance="subtle" shape="circular" size="small" disabled={saving} onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </div>
      </PopoverSurface>
    </Popover>
  );
}
